import { Component, ViewChild } from '@angular/core';
import { BaseKnowledgeCatalogService } from './services/base-knowledge-catalog.service';
import { I18nService } from 'i18n/i18n.service';
import { IKBCatalogCategory, IKBCatalogCategoryLearned } from './interfaces/IBase-knowledge-catalog';
import { BzgToastService, BzgToastType } from 'bz-genome-angular';
import { ShowToastService } from 'app/shared/services/showToast.service';
import { BaseKnowledgeCatalogBizagiDataComponent } from './base-knowledge-catalog-bizagi-data/base-knowledge-catalog-bizagi-data.component';

@Component({
  selector: 'biz-base-knowledge-catalog',
  templateUrl: './base-knowledge-catalog.component.html',
  styleUrls: ['./base-knowledge-catalog.component.scss']
})

export class BaseKnowledgeCatalogComponent {
  selectedTab: number = 0;
  showLoading: boolean = true;
  showLearned: boolean = false;
  showNewBizagiDataView: boolean = false;
  showFiles: boolean = false;
  catalogCreationEnabled: boolean = false;
  isChangingCategory: boolean = false;
  inputSearch = '';
  
  // Individual loading states
  isLoadingFiles: boolean = false;
  isLoadingLearned: boolean = false;
  filesLoaded: boolean = false;
  learnedLoaded: boolean = false;

  categoryList: IKBCatalogCategory[] = [];
  selectedCategory: IKBCatalogCategory;
  allCategory: IKBCatalogCategory = { DisplayName: 'All', Description: 'All', Id: 'All', Expanded: false, EditMode: false, KBCatalogs: [] };
  filteredCategoryList: IKBCatalogCategory[] = [];

  categoryListLearned: IKBCatalogCategoryLearned[] = [];
  filteredCategoryListLearned: IKBCatalogCategoryLearned[] = [];

  @ViewChild(BaseKnowledgeCatalogBizagiDataComponent) bizagiDataComponent: BaseKnowledgeCatalogBizagiDataComponent;

  constructor(private _baseKnowledgeCatalogService: BaseKnowledgeCatalogService,
    private _i18nService: I18nService,
    private _toastService: BzgToastService,
    private _showToastService: ShowToastService
  ) { }

  ngOnInit(): void {
    this.showLoading = true;
    this.loadFeatures();
  }

  loadFeatures(): void {
    this._baseKnowledgeCatalogService.checkBaseKnowledgeFeaturesEnabled().subscribe((res) => {
      this.showLearned = res.EnableLearnedKnowledge;
      this.showFiles = res.EnableFilesKnowledge;
      this.showNewBizagiDataView = res.EnableNewBizagiDataView;
      
      // Determine first available tab
      this.selectedTab = this.getFirstAvailableTab();
      
      this.showLoading = false;
      
      // Load data for the initial tab
      this.loadDataForCurrentTab();
    });
  }

  getFirstAvailableTab(): number {
    if (this.showFiles) return this.getTabIndex('files');
    if (this.showLearned) return this.getTabIndex('learned');
    if (this.showNewBizagiDataView) return this.getTabIndex('bizagiData');
    return 0;
  }

  loadDataForCurrentTab(): void {
    // Load files data if on files tab
    if (this.selectedTab === this.getTabIndex('files') && !this.filesLoaded && !this.isLoadingFiles) {
      this.loadFilesData();
    }
    // Load learned data if on learned/bizagi data tab
    else if ((this.selectedTab === this.getTabIndex('learned') || 
              this.selectedTab === this.getTabIndex('bizagiData')) && 
             !this.learnedLoaded && !this.isLoadingLearned) {
      this.loadLearnedData();
    }
  }

  loadFilesData(): void {
    if (this.isLoadingFiles) return; // Prevent duplicate calls
    
    this.isLoadingFiles = true;
    this._baseKnowledgeCatalogService.getBaseKnowledgeCatalog().subscribe(
      (res) => {
        if (res.length > 0) {
          this.categoryList = res;
          this.setArtifactStatusLocalized();
          this.selectedCategory = this.allCategory;
          this.filteredCategoryList = this.categoryList;
          this.collapsedList();
        }
        this.filesLoaded = true;
        this.isLoadingFiles = false;
      },
      () => {
        // Silently handle errors during loading
        this.isLoadingFiles = false;
      }
    );
  }

  loadLearnedData(): void {
    if (this.isLoadingLearned) return; // Prevent duplicate calls
    
    this.isLoadingLearned = true;
    this._baseKnowledgeCatalogService.getBaseKnowledgeCatalogLearned().subscribe(
      (res) => {
        if (res.length > 0) {
          this.categoryListLearned = res;
          this.filteredCategoryListLearned = this.categoryListLearned;
          this.collapsedList();
        }
        this.learnedLoaded = true;
        this.isLoadingLearned = false;
      },
      () => {
        // Silently handle errors during loading
        this.isLoadingLearned = false;
      }
    );
  }

  getTabIndex(tabType): number {
    let index = 0;
    if (tabType === 'files') {
      return this.showFiles ? index : -1;
    } else if (tabType === 'learned') {
      if (this.showFiles) 
        index++;
      return this.showLearned ? index : -1;
    } else if (tabType === 'bizagiData') {
      if (this.showFiles) 
        index += 2;
      return this.showNewBizagiDataView ? index : -1;
    }
    
    return -1;
  }

  getBaseKnowledgeCatalog() {
    this.loadFilesData();
  }

  getBaseKnowledgeCatalogLearned() {
    this.loadLearnedData();
  }

  setArtifactStatusLocalized(): void {
    this.categoryList.forEach(category => {
      category.KBCatalogs.forEach(catalog => {
        catalog.KBArtifacts.forEach(artifact => {
          artifact.ArtifactStatus = artifact.ArtifactStatus.toUpperCase();
          artifact.ArtifactStatusLocalized = this._i18nService.instant('BASEKNOWLEDGECATALOG.ARTIFACT_STATUS.' + artifact.ArtifactStatus.toLocaleUpperCase());
        });
      });
    });
  }

  checkBaseKnowledgeFeaturesEnabled() {
    this._baseKnowledgeCatalogService.checkBaseKnowledgeFeaturesEnabled().subscribe((res) => {
      this.showLearned = res.EnableLearnedKnowledge;
      this.showFiles = res.EnableFilesKnowledge;
      this.showNewBizagiDataView = res.EnableNewBizagiDataView;
    });
  }

  onEnterSearch(): void {
    this.onSearch(this.inputSearch);
  }

  onPaste(event: any): void {
    // Use setTimeout to allow the pasted value to update in the input
    setTimeout(() => {
      const pastedText = event.target?.value || event.clipboardData?.getData('text') || '';
      this.onSearch(pastedText);
    }, 10);
  }

  onSearch(searchString: string): void {
    if (this.isChangingCategory) return;
    
    this.inputSearch = searchString;
    // Normalize the search string: trim, replace multiple spaces with single space, and convert to lowercase
    const normalizedSearchString = searchString.trim().replace(/\s+/g, ' ').toLowerCase();

    // Reset selection in bizagi data
    this.resetBizagiDataSelection();

    if (normalizedSearchString === '') {
      // If there is no search text, restore the view according to the selected category
      if (this.selectedCategory && this.selectedCategory.Id === 'All') {
        if (this.categoryList.length > 0) {
          this.filteredCategoryList = this.categoryList;
        }
        if (this.categoryListLearned.length > 0) {
          this.filteredCategoryListLearned = this.categoryListLearned;
        }
      } else if (this.selectedCategory) {
        if (this.categoryList.length > 0) {
          this.filteredCategoryList = this.categoryList.filter(category => category.Id === this.selectedCategory.Id);
        }
        if (this.categoryListLearned.length > 0) {
          this.filteredCategoryListLearned = this.categoryListLearned.filter(category => category.Id === this.selectedCategory.Id);
        }
      }
      if (this.filteredCategoryList.length > 0 || this.filteredCategoryListLearned.length > 0) {
        this.collapsedList();
      }
      return;
    } else {
      // Tab 0 - Search in files
      if (this.selectedTab === 0 && this.filesLoaded) {
        this.searchInFiles(normalizedSearchString);
      }
      // Tab 1 - Search in Bizagi data
      else if ((this.selectedTab === 1 || this.selectedTab === 2) && this.learnedLoaded) {
        this.searchInBizagiData(normalizedSearchString);
      }
    }
  }

  onCategoryChange(event: IKBCatalogCategory): void {
    this.isChangingCategory = true; 
    this.inputSearch = '';
    
    // Reset selection in bizagi data
    this.resetBizagiDataSelection();
    
    // Load data if not yet loaded for current tab
    this.loadDataForCurrentTab();
    
    // Only filter if data is available
    if (event.Id === 'All') {
      if (this.categoryList.length > 0) {
        this.filteredCategoryList = this.categoryList;
      }
      if (this.categoryListLearned.length > 0) {
        this.filteredCategoryListLearned = this.categoryListLearned;
      }
      if (this.filteredCategoryList.length > 0 || this.filteredCategoryListLearned.length > 0) {
        this.collapsedList();
      }
    } else {
      if (this.categoryList.length > 0) {
        this.filteredCategoryList = this.categoryList.filter(category => category.Id === event.Id);
      }
      if (this.categoryListLearned.length > 0) {
        this.filteredCategoryListLearned = this.categoryListLearned.filter(category => category.Id === event.Id);
      }
      if (this.filteredCategoryList.length > 0 || this.filteredCategoryListLearned.length > 0) {
        this.collapsedList();
      }
    }
    
    setTimeout(() => {
      this.isChangingCategory = false;
    }, 0);
  }

  collapsedList(): void {
    this.filteredCategoryList.forEach((category, categoryIndex) => {
      if (categoryIndex === 0) {
        category.Expanded = true;
        category.KBCatalogs.forEach((catalog, catalogIndex) => {
          catalog.Expanded = catalogIndex === 0;
        });
      } else {
        category.Expanded = false;
        category.KBCatalogs.forEach(catalog => {
          catalog.Expanded = false;
        });
      }
    });
    this.filteredCategoryListLearned.forEach((category, categoryIndex) => {
      if (categoryIndex === 0) {
        category.Expanded = true;
        category.KBLearned.forEach((KBLearned, KBLearnedIndex) => {
          KBLearned.Expanded = KBLearnedIndex === 0;
        });
      } else {
        category.Expanded = false;
        category.KBLearned.forEach(KBLearned => {
          KBLearned.Expanded = false;
        });
      }
    });
  }

  refreshKBLearned(kbLearnedId: Guid): void {
    this._baseKnowledgeCatalogService.retryLearnedVectorization(kbLearnedId).subscribe((res) => {
      if (res) {
        this._showToastService.showToast(this._i18nService.instant('BASEKNOWLEDGECATALOG.DATA_RETRY'), 'success');
      }
    });
  }
  refreshKBLearnedV2(kbLearnedId: Guid): void {
    this._baseKnowledgeCatalogService.getDetailsKBLearned(kbLearnedId).subscribe((res) => {
      if (res) {
        this._showToastService.showToast(this._i18nService.instant('BASEKNOWLEDGECATALOG.DATA_RETRY'), 'success');
      }
    });
  }

  reloadCatalogs(event: boolean): void {
    if (event)
      this.getBaseKnowledgeCatalog();
  }

  selectTab(index: number): void {
    this.selectedTab = index;
    
    // Load data for the new tab if not already loaded
    this.loadDataForCurrentTab();
    
    // If there is text in the search box, apply the filter when changing tabs
    if (this.inputSearch && this.inputSearch.trim() !== '') {
      this.onSearch(this.inputSearch);
    }
  }

  private searchInFiles(lowerCaseSearchString: string): void {
    // Determine the search base according to the selected category
    const baseCategories = this.selectedCategory.Id === 'All' 
      ? this.categoryList 
      : this.categoryList.filter(category => category.Id === this.selectedCategory.Id);
    
    const baseCategoriesLearned = this.selectedCategory.Id === 'All'
      ? this.categoryListLearned
      : this.categoryListLearned.filter(category => category.Id === this.selectedCategory.Id);

    let searchResultCategories = baseCategories.filter(category => {
      const matchingCatalogs = category.KBCatalogs.filter(catalog => {
        const matchingArtifacts = catalog.KBArtifacts.filter(artifact => {
          const normalizedDisplayName = artifact.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
          const normalizedDescription = artifact.Description ? artifact.Description.trim().replace(/\s+/g, ' ').toLowerCase() : '';
          return normalizedDisplayName.includes(lowerCaseSearchString) || normalizedDescription.includes(lowerCaseSearchString);
        });
        const normalizedCatalogName = catalog.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
        catalog.Expanded = matchingArtifacts.length > 0 || normalizedCatalogName.includes(lowerCaseSearchString);
        return catalog.Expanded;
      });
      const normalizedCategoryName = category.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
      category.Expanded = matchingCatalogs.length > 0 || normalizedCategoryName.includes(lowerCaseSearchString);
      return category.Expanded;
    });

    this.filteredCategoryList = searchResultCategories;

    let searchResultCategoriesLearned = baseCategoriesLearned.filter(category => {
      const matchingKBLearned = category.KBLearned.filter(kblearned => {
        const normalizedDisplayName = kblearned.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
        const normalizedDescription = kblearned.Description ? kblearned.Description.trim().replace(/\s+/g, ' ').toLowerCase() : '';
        kblearned.Expanded = normalizedDisplayName.includes(lowerCaseSearchString) || normalizedDescription.includes(lowerCaseSearchString);
        return kblearned.Expanded;
      });
      const normalizedCategoryName = category.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
      category.Expanded = matchingKBLearned.length > 0 || normalizedCategoryName.includes(lowerCaseSearchString);
      return category.Expanded;
    });
    this.filteredCategoryListLearned = searchResultCategoriesLearned;
  }

  private searchInFilesWithCategory(lowerCaseSearchString: string): void {
    // Apply search on lists already filtered by category
    let searchResultCategories = this.filteredCategoryList.filter(category => {
      const matchingCatalogs = category.KBCatalogs.filter(catalog => {
        const matchingArtifacts = catalog.KBArtifacts.filter(artifact => {
          const normalizedDisplayName = artifact.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
          const normalizedDescription = artifact.Description ? artifact.Description.trim().replace(/\s+/g, ' ').toLowerCase() : '';
          return normalizedDisplayName.includes(lowerCaseSearchString) || normalizedDescription.includes(lowerCaseSearchString);
        });
        const normalizedCatalogName = catalog.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
        catalog.Expanded = matchingArtifacts.length > 0 || normalizedCatalogName.includes(lowerCaseSearchString);
        return catalog.Expanded;
      });
      const normalizedCategoryName = category.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
      category.Expanded = matchingCatalogs.length > 0 || normalizedCategoryName.includes(lowerCaseSearchString);
      return category.Expanded;
    });

    this.filteredCategoryList = searchResultCategories;

    let searchResultCategoriesLearned = this.filteredCategoryListLearned.filter(category => {
      const matchingKBLearned = category.KBLearned.filter(kblearned => {
        const normalizedDisplayName = kblearned.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
        const normalizedDescription = kblearned.Description ? kblearned.Description.trim().replace(/\s+/g, ' ').toLowerCase() : '';
        kblearned.Expanded = normalizedDisplayName.includes(lowerCaseSearchString) || normalizedDescription.includes(lowerCaseSearchString);
        return kblearned.Expanded;
      });
      const normalizedCategoryName = category.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
      category.Expanded = matchingKBLearned.length > 0 || normalizedCategoryName.includes(lowerCaseSearchString);
      return category.Expanded;
    });
    this.filteredCategoryListLearned = searchResultCategoriesLearned;
  }

  private searchInBizagiData(lowerCaseSearchString: string): void {
    // Determine the search base according to the selected category
    const baseCategories = this.selectedCategory.Id === 'All' 
      ? this.categoryList 
      : this.categoryList.filter(category => category.Id === this.selectedCategory.Id);
    
    const baseCategoriesLearned = this.selectedCategory.Id === 'All'
      ? this.categoryListLearned
      : this.categoryListLearned.filter(category => category.Id === this.selectedCategory.Id);

    let searchResultCategories = baseCategories.map(category => {
      const matchingCatalogs = category.KBCatalogs.map(catalog => {
        const matchingArtifacts = catalog.KBArtifacts.filter(artifact => {
          const normalizedDisplayName = artifact.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
          const normalizedDescription = artifact.Description ? artifact.Description.trim().replace(/\s+/g, ' ').toLowerCase() : '';
          return normalizedDisplayName.includes(lowerCaseSearchString) || normalizedDescription.includes(lowerCaseSearchString);
        });
        
        const normalizedCatalogName = catalog.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
        // Only include the catalog if it has matching artifacts or if the catalog name matches
        if (matchingArtifacts.length > 0 || normalizedCatalogName.includes(lowerCaseSearchString)) {
          return {
            ...catalog,
            KBArtifacts: normalizedCatalogName.includes(lowerCaseSearchString) ? catalog.KBArtifacts : matchingArtifacts,
            Expanded: true
          };
        }
        return null;
      }).filter(catalog => catalog !== null);

      const normalizedCategoryName = category.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
      // Only include the category if it has matching catalogs or if the category name matches
      if (matchingCatalogs.length > 0 || normalizedCategoryName.includes(lowerCaseSearchString)) {
        return {
          ...category,
          KBCatalogs: normalizedCategoryName.includes(lowerCaseSearchString) ? category.KBCatalogs : matchingCatalogs,
          Expanded: true
        };
      }
      return null;
    }).filter(category => category !== null);

    this.filteredCategoryList = searchResultCategories;

    let searchResultCategoriesLearned = baseCategoriesLearned.map(category => {
      const matchingKBLearned = category.KBLearned.filter(kblearned => {
        const normalizedDisplayName = kblearned.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
        const normalizedDescription = kblearned.Description ? kblearned.Description.trim().replace(/\s+/g, ' ').toLowerCase() : '';
        return normalizedDisplayName.includes(lowerCaseSearchString) || normalizedDescription.includes(lowerCaseSearchString);
      });

      const normalizedCategoryName = category.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
      // Only include the category if it has matching KBLearned elements or if the category name matches
      if (matchingKBLearned.length > 0 || normalizedCategoryName.includes(lowerCaseSearchString)) {
        const filteredKBLearned = normalizedCategoryName.includes(lowerCaseSearchString) 
          ? category.KBLearned 
          : matchingKBLearned;
        
        return {
          ...category,
          KBLearned: filteredKBLearned.map(kblearned => {
            const normalizedDisplayName = kblearned.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
            const normalizedDescription = kblearned.Description ? kblearned.Description.trim().replace(/\s+/g, ' ').toLowerCase() : '';
            return {
              ...kblearned,
              Expanded: normalizedDisplayName.includes(lowerCaseSearchString) || normalizedDescription.includes(lowerCaseSearchString)
            };
          }),
          Expanded: true
        };
      }
      return null;
    }).filter(category => category !== null);

    this.filteredCategoryListLearned = searchResultCategoriesLearned;
  }

  private searchInBizagiDataWithCategory(lowerCaseSearchString: string): void {
    // Apply search on lists already filtered by category
    let searchResultCategories = this.filteredCategoryList.map(category => {
      const matchingCatalogs = category.KBCatalogs.map(catalog => {
        const matchingArtifacts = catalog.KBArtifacts.filter(artifact => {
          const normalizedDisplayName = artifact.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
          const normalizedDescription = artifact.Description ? artifact.Description.trim().replace(/\s+/g, ' ').toLowerCase() : '';
          return normalizedDisplayName.includes(lowerCaseSearchString) || normalizedDescription.includes(lowerCaseSearchString);
        });
        
        const normalizedCatalogName = catalog.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
        // Only include the catalog if it has matching artifacts or if the catalog name matches
        if (matchingArtifacts.length > 0 || normalizedCatalogName.includes(lowerCaseSearchString)) {
          return {
            ...catalog,
            KBArtifacts: normalizedCatalogName.includes(lowerCaseSearchString) ? catalog.KBArtifacts : matchingArtifacts,
            Expanded: true
          };
        }
        return null;
      }).filter(catalog => catalog !== null);

      const normalizedCategoryName = category.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
      // Only include the category if it has matching catalogs or if the category name matches
      if (matchingCatalogs.length > 0 || normalizedCategoryName.includes(lowerCaseSearchString)) {
        return {
          ...category,
          KBCatalogs: normalizedCategoryName.includes(lowerCaseSearchString) ? category.KBCatalogs : matchingCatalogs,
          Expanded: true
        };
      }
      return null;
    }).filter(category => category !== null);

    this.filteredCategoryList = searchResultCategories;

    let searchResultCategoriesLearned = this.filteredCategoryListLearned.map(category => {
      const matchingKBLearned = category.KBLearned.filter(kblearned => {
        const normalizedDisplayName = kblearned.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
        const normalizedDescription = kblearned.Description ? kblearned.Description.trim().replace(/\s+/g, ' ').toLowerCase() : '';
        return normalizedDisplayName.includes(lowerCaseSearchString) || normalizedDescription.includes(lowerCaseSearchString);
      });

      const normalizedCategoryName = category.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
      // Only include the category if it has matching KBLearned elements or if the category name matches
      if (matchingKBLearned.length > 0 || normalizedCategoryName.includes(lowerCaseSearchString)) {
        const filteredKBLearned = normalizedCategoryName.includes(lowerCaseSearchString) 
          ? category.KBLearned 
          : matchingKBLearned;
        
        return {
          ...category,
          KBLearned: filteredKBLearned.map(kblearned => {
            const normalizedDisplayName = kblearned.DisplayName.trim().replace(/\s+/g, ' ').toLowerCase();
            const normalizedDescription = kblearned.Description ? kblearned.Description.trim().replace(/\s+/g, ' ').toLowerCase() : '';
            return {
              ...kblearned,
              Expanded: normalizedDisplayName.includes(lowerCaseSearchString) || normalizedDescription.includes(lowerCaseSearchString)
            };
          }),
          Expanded: true
        };
      }
      return null;
    }).filter(category => category !== null);

    this.filteredCategoryListLearned = searchResultCategoriesLearned;
  }
  
  private resetBizagiDataSelection(): void {
    if (this.bizagiDataComponent) {
      this.bizagiDataComponent.resetSelection();
    }
  }

  compareCategory(c1: IKBCatalogCategory, c2: IKBCatalogCategory): boolean {
    return c1 && c2 ? c1.Id === c2.Id : c1 === c2;
  }

  get isDevelopment(): boolean {
    const environmentType = sessionStorage.getItem('environmentType');
    return environmentType?.toLowerCase() === 'development';
  }

  get isCurrentTabLoading(): boolean {
    if (this.selectedTab === this.getTabIndex('files')) {
      return this.isLoadingFiles;
    } else if (this.selectedTab === this.getTabIndex('learned') || 
               this.selectedTab === this.getTabIndex('bizagiData')) {
      return this.isLoadingLearned;
    }
    return false;
  }

  get currentCategoryList(): IKBCatalogCategory[] | IKBCatalogCategoryLearned[] {
    if (this.selectedTab === this.getTabIndex('files')) {
      return this.categoryList;
    } else if (this.selectedTab === this.getTabIndex('learned') || 
               this.selectedTab === this.getTabIndex('bizagiData')) {
      return this.categoryListLearned;
    }
    return [];
  }
}
